/**
 * DancerModel: Loads the SK_Dancer.fbx rigged 3D mesh at the playhead (Z=0).
 *
 * Features:
 *   - Procedural beat-synced dance animation (hip sway, weight shift, knee bend,
 *     shoulder counter-rotation, head bob/tilt, spine twist)
 *   - Arms track upcoming notes per hand (same logic as HumanFigure)
 *   - Hand bones follow rail paths when notes have rails
 *   - Green (one_hand_special) notes: one random hand per sequence
 *   - Both_hands_special (yellow): both hands track to same position
 *   - Scales to match mannequin height setting
 *
 * The FBX has NO embedded animations, so all movement is driven
 * procedurally via group-level transforms (sway, bob, twist) each frame.
 * Individual bone manipulation is avoided due to FBX pre-rotation complexity;
 * only upper-arm bones are rotated for IK-like note tracking.
 *
 * Uses @react-three/drei's useFBX for loading.
 * Skeleton bones are discovered automatically by traversal.
 */

import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { useFrame } from '@react-three/fiber';
import { useFBX } from '@react-three/drei';
import * as THREE from 'three';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { useEditorStore, getNoteColorsFromSettings } from '../../store/editorStore';
import { GRID_SCALE } from '../../types/beatmap';
import type { Note } from '../../types/beatmap';

// ── Preload the dancer FBX at module scope ──────────────────────────────────
const DANCER_FBX_PATH = '/models/dancer/SK_Dancer.fbx';
useFBX.preload(DANCER_FBX_PATH);

// ── Constants (must match TunnelScene / HumanFigure) ─────────────────────────
const NOTE_Y_OFFSET = 1.8;
const FIGURE_Z = 0.12;

// Default rest positions (relaxed at sides) — same as HumanFigure
const REST_LEFT = new THREE.Vector3(-0.45, 1.1, 0.15);
const REST_RIGHT = new THREE.Vector3(0.45, 1.1, 0.15);

// ── Note Lookup (same logic as HumanFigure) ─────────────────────────────────

function binarySearchBeat(notes: Note[], targetBeat: number): number {
  let lo = 0;
  let hi = notes.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (notes[mid].time_beats < targetBeat) lo = mid + 1;
    else hi = mid - 1;
  }
  return lo;
}

function findNextNoteForHand(
  notes: Note[], currentBeat: number, hand: 'left' | 'right',
  anticipationBeats: number, greenHand: 'left' | 'right' | 'none',
): Note | null {
  const searchStart = currentBeat - anticipationBeats;
  const searchEnd = currentBeat + 4;
  const startIdx = binarySearchBeat(notes, searchStart);
  for (let i = startIdx; i < notes.length; i++) {
    const note = notes[i];
    if (note.time_beats > searchEnd) break;
    if (note.hand === hand) return note;
    if (note.note_type === 'both_hands_special') return note;
    if (note.note_type === 'one_hand_special') {
      if (greenHand === hand || greenHand === 'none') return note;
    }
  }
  return null;
}

function findActiveRailNote(
  notes: Note[], currentBeat: number, hand: 'left' | 'right',
  bpm: number, greenHand: 'left' | 'right' | 'none',
): Note | null {
  const startIdx = binarySearchBeat(notes, currentBeat);
  for (let i = startIdx - 1; i >= 0; i--) {
    const note = notes[i];
    if (currentBeat - note.time_beats > 16) break;
    if (!note.has_rail || !note.rail_nodes || note.rail_nodes.length === 0) continue;
    const matchesHand = note.hand === hand ||
      note.note_type === 'both_hands_special' ||
      (note.note_type === 'one_hand_special' && (greenHand === hand || greenHand === 'none'));
    if (!matchesHand) continue;
    const lastNode = note.rail_nodes[note.rail_nodes.length - 1];
    const lastNodeBeat = ((lastNode[2] / 0.02) / 1000.0) * (bpm / 60.0);
    if (lastNodeBeat > currentBeat) return note;
  }
  return null;
}

function findNextGreenNote(
  notes: Note[], currentBeat: number, anticipationBeats: number,
): Note | null {
  const searchStart = currentBeat - anticipationBeats;
  const searchEnd = currentBeat + 4;
  const startIdx = binarySearchBeat(notes, searchStart);
  for (let i = startIdx; i < notes.length; i++) {
    const note = notes[i];
    if (note.time_beats > searchEnd) break;
    if (note.note_type === 'one_hand_special') return note;
  }
  return null;
}

function noteToWorld(note: Note): THREE.Vector3 {
  return new THREE.Vector3(
    note.position[0] * GRID_SCALE * 10,
    note.position[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET,
    0,
  );
}

function getRailPositionAtBeat(note: Note, currentBeat: number, bpm: number): THREE.Vector3 | null {
  if (!note.has_rail || !note.rail_nodes || note.rail_nodes.length < 1) return null;
  const headBeat = note.time_beats;
  const points: { beat: number; x: number; y: number }[] = [{
    beat: headBeat,
    x: note.position[0] * GRID_SCALE * 10,
    y: note.position[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET,
  }];
  for (const node of note.rail_nodes) {
    const nodeBeats = ((node[2] / 0.02) / 1000.0) * (bpm / 60.0);
    points.push({ beat: nodeBeats, x: node[0] * GRID_SCALE * 10, y: node[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET });
  }
  if (currentBeat <= headBeat) return new THREE.Vector3(points[0].x, points[0].y, 0);
  const lastPoint = points[points.length - 1];
  if (currentBeat >= lastPoint.beat) return null;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]; const b = points[i + 1];
    if (currentBeat >= a.beat && currentBeat < b.beat) {
      const t = (b.beat - a.beat) <= 0 ? 0 : (currentBeat - a.beat) / (b.beat - a.beat);
      return new THREE.Vector3(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, 0);
    }
  }
  return new THREE.Vector3(lastPoint.x, lastPoint.y, 0);
}

// ── Bone discovery helper ───────────────────────────────────────────────────

/** Common bone name patterns across FBX rigs */
const LEFT_HAND_PATTERNS = [
  /left.*hand/i, /hand.*l$/i, /l_hand/i, /lhand/i, /hand_l/i,
  /left.*wrist/i, /wrist.*l$/i, /l_wrist/i,
];
const RIGHT_HAND_PATTERNS = [
  /right.*hand/i, /hand.*r$/i, /r_hand/i, /rhand/i, /hand_r/i,
  /right.*wrist/i, /wrist.*r$/i, /r_wrist/i,
];
const LEFT_ARM_PATTERNS = [
  /left.*forearm/i, /forearm.*l$/i, /l_forearm/i,
  /left.*elbow/i, /left.*lowerarm/i, /lowerarm.*l$/i,
];
const RIGHT_ARM_PATTERNS = [
  /right.*forearm/i, /forearm.*r$/i, /r_forearm/i,
  /right.*elbow/i, /right.*lowerarm/i, /lowerarm.*r$/i,
];
const LEFT_UPPER_ARM_PATTERNS = [
  /left.*upperarm/i, /upperarm.*l$/i, /l_upperarm/i,
  /left.*shoulder/i, /shoulder.*l$/i, /l_shoulder/i, /left.*arm$/i, /^arm.*l$/i,
];
const RIGHT_UPPER_ARM_PATTERNS = [
  /right.*upperarm/i, /upperarm.*r$/i, /r_upperarm/i,
  /right.*shoulder/i, /shoulder.*r$/i, /r_shoulder/i, /right.*arm$/i, /^arm.*r$/i,
];
const HIPS_PATTERNS = [/^hips$/i, /^pelvis$/i, /^root$/i, /^hip$/i];

// Spine / chest / head patterns
const SPINE_PATTERNS = [/^spine$/i, /^spine1$/i, /^spine_01$/i, /spine.*1/i, /^torso$/i];
const SPINE2_PATTERNS = [/^spine2$/i, /^spine_02$/i, /spine.*2/i, /^chest$/i];
const HEAD_PATTERNS = [/^head$/i, /^head_end$/i];
const NECK_PATTERNS = [/^neck$/i, /^neck_01$/i];

// Leg bone patterns
const LEFT_THIGH_PATTERNS = [
  /left.*thigh/i, /thigh.*l$/i, /l_thigh/i,
  /left.*upleg/i, /upleg.*l$/i, /l_upleg/i,
  /left.*upper.*leg/i, /upper.*leg.*l$/i, /l_upper.*leg/i,
  /left.*hip/i,
];
const RIGHT_THIGH_PATTERNS = [
  /right.*thigh/i, /thigh.*r$/i, /r_thigh/i,
  /right.*upleg/i, /upleg.*r$/i, /r_upleg/i,
  /right.*upper.*leg/i, /upper.*leg.*r$/i, /r_upper.*leg/i,
  /right.*hip/i,
];
const LEFT_SHIN_PATTERNS = [
  /left.*calf/i, /calf.*l$/i, /l_calf/i,
  /left.*shin/i, /shin.*l$/i, /l_shin/i,
  /left.*leg$/i, /leg.*l$/i, /l_leg$/i,
  /left.*low.*leg/i, /low.*leg.*l$/i,
];
const RIGHT_SHIN_PATTERNS = [
  /right.*calf/i, /calf.*r$/i, /r_calf/i,
  /right.*shin/i, /shin.*r$/i, /r_shin/i,
  /right.*leg$/i, /leg.*r$/i, /r_leg$/i,
  /right.*low.*leg/i, /low.*leg.*r$/i,
];
const LEFT_FOOT_PATTERNS = [
  /left.*foot/i, /foot.*l$/i, /l_foot/i, /lfoot/i,
];
const RIGHT_FOOT_PATTERNS = [
  /right.*foot/i, /foot.*r$/i, /r_foot/i, /rfoot/i,
];

function findBoneByPatterns(skeleton: THREE.Bone[], patterns: RegExp[]): THREE.Bone | null {
  for (const pat of patterns) {
    const found = skeleton.find(b => pat.test(b.name));
    if (found) return found;
  }
  return null;
}

function findAllBones(root: THREE.Object3D): THREE.Bone[] {
  const bones: THREE.Bone[] = [];
  root.traverse(child => {
    if ((child as THREE.Bone).isBone) bones.push(child as THREE.Bone);
  });
  return bones;
}

// ── Glow hand sphere ────────────────────────────────────────────────────────
// Small emissive sphere placed at each hand to show note tracking visually

function HandGlow({ colorRef, defaultColor }: { colorRef: React.RefObject<THREE.Mesh | null>; defaultColor: string }) {
  return (
    <mesh ref={colorRef}>
      <sphereGeometry args={[0.04, 10, 8]} />
      <meshStandardMaterial
        color={defaultColor} emissive={defaultColor} emissiveIntensity={0.7}
        transparent opacity={0.8}
      />
    </mesh>
  );
}

// ── Bone reference interface ────────────────────────────────────────────────

interface BoneRefs {
  leftHand: THREE.Bone | null;
  rightHand: THREE.Bone | null;
  leftForearm: THREE.Bone | null;
  rightForearm: THREE.Bone | null;
  leftUpperArm: THREE.Bone | null;
  rightUpperArm: THREE.Bone | null;
  hips: THREE.Bone | null;
  spine: THREE.Bone | null;
  spine2: THREE.Bone | null;
  neck: THREE.Bone | null;
  head: THREE.Bone | null;
  leftThigh: THREE.Bone | null;
  rightThigh: THREE.Bone | null;
  leftShin: THREE.Bone | null;
  rightShin: THREE.Bone | null;
  leftFoot: THREE.Bone | null;
  rightFoot: THREE.Bone | null;
  allBones: THREE.Bone[];
}

// ── Main Component ──────────────────────────────────────────────────────────

export default function DancerModel() {
  const groupRef = useRef<THREE.Group>(null);

  // Note colors for hand glow
  const noteColorLeft = useEditorStore(s => s.gameplaySettings.noteColorLeft);
  const noteColorRight = useEditorStore(s => s.gameplaySettings.noteColorRight);

  // Refs for hand glow spheres
  const leftHandGlowRef = useRef<THREE.Mesh>(null);
  const rightHandGlowRef = useRef<THREE.Mesh>(null);

  // Bone references (discovered after load)
  const bonesRef = useRef<BoneRefs>({
    leftHand: null, rightHand: null,
    leftForearm: null, rightForearm: null,
    leftUpperArm: null, rightUpperArm: null,
    hips: null, spine: null, spine2: null,
    neck: null, head: null,
    leftThigh: null, rightThigh: null,
    leftShin: null, rightShin: null,
    leftFoot: null, rightFoot: null,
    allBones: [],
  });

  // Dance phase accumulator
  const dancePhase = useRef(0);

  // Smoothed hand positions (same approach as HumanFigure)
  const leftHandSmoothed = useRef(REST_LEFT.clone());
  const rightHandSmoothed = useRef(REST_RIGHT.clone());
  const leftHandTarget = useRef(REST_LEFT.clone());
  const rightHandTarget = useRef(REST_RIGHT.clone());

  // Green note hand assignment
  const greenHandAssignment = useRef<'left' | 'right' | 'none'>('none');
  const lastGreenNoteId = useRef<string>('');

  // Track what notes are active (for hand glow colors)
  const leftNoteRef = useRef<Note | null>(null);
  const rightNoteRef = useRef<Note | null>(null);

  // Load FBX model
  const fbx = useFBX(DANCER_FBX_PATH);
  const mannequinSettings = useEditorStore(s => s.mannequinSettings);
  const setArmSpanMeters = useEditorStore(s => s.setArmSpanMeters);

  // Clone the scene using SkeletonUtils.clone (preserves skeleton bindings
  // so that SkinnedMesh → Skeleton links work properly).
  const scene = useMemo(() => {
    const cloned = cloneSkeleton(fbx);

    // No root rotation needed — FBX loader handles Z-up → Y-up via bone pre-rotations.

    cloned.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        mesh.material = new THREE.MeshStandardMaterial({
          color: '#9F7AEA',
          emissive: '#7C3AED',
          emissiveIntensity: 0.25,
          transparent: true,
          opacity: 0.6,
          wireframe: false,
          side: THREE.DoubleSide,
        });
        mesh.castShadow = false;
        mesh.receiveShadow = false;
      }
    });
    return cloned;
  }, [fbx]);

  // Discover bones after scene is ready & save rest pose
  useEffect(() => {
    if (!scene) return;
    const allBones = findAllBones(scene);

    // Log bone names for debugging (only in development)
    if (process.env.NODE_ENV === 'development' && allBones.length > 0) {
      console.log('[DancerModel] Found bones:', allBones.map(b => b.name));
    }

    const refs: BoneRefs = {
      leftHand: findBoneByPatterns(allBones, LEFT_HAND_PATTERNS),
      rightHand: findBoneByPatterns(allBones, RIGHT_HAND_PATTERNS),
      leftForearm: findBoneByPatterns(allBones, LEFT_ARM_PATTERNS),
      rightForearm: findBoneByPatterns(allBones, RIGHT_ARM_PATTERNS),
      leftUpperArm: findBoneByPatterns(allBones, LEFT_UPPER_ARM_PATTERNS),
      rightUpperArm: findBoneByPatterns(allBones, RIGHT_UPPER_ARM_PATTERNS),
      hips: findBoneByPatterns(allBones, HIPS_PATTERNS),
      spine: findBoneByPatterns(allBones, SPINE_PATTERNS),
      spine2: findBoneByPatterns(allBones, SPINE2_PATTERNS),
      neck: findBoneByPatterns(allBones, NECK_PATTERNS),
      head: findBoneByPatterns(allBones, HEAD_PATTERNS),
      leftThigh: findBoneByPatterns(allBones, LEFT_THIGH_PATTERNS),
      rightThigh: findBoneByPatterns(allBones, RIGHT_THIGH_PATTERNS),
      leftShin: findBoneByPatterns(allBones, LEFT_SHIN_PATTERNS),
      rightShin: findBoneByPatterns(allBones, RIGHT_SHIN_PATTERNS),
      leftFoot: findBoneByPatterns(allBones, LEFT_FOOT_PATTERNS),
      rightFoot: findBoneByPatterns(allBones, RIGHT_FOOT_PATTERNS),
      allBones,
    };

    bonesRef.current = refs;

    if (process.env.NODE_ENV === 'development') {
      console.log('[DancerModel] Matched bones:', {
        hips: refs.hips?.name, spine: refs.spine?.name, spine2: refs.spine2?.name,
        head: refs.head?.name, leftThigh: refs.leftThigh?.name, leftShin: refs.leftShin?.name,
        leftUpperArm: refs.leftUpperArm?.name, leftHand: refs.leftHand?.name,
      });
    }
  }, [scene]);

  // Compute bounding box AFTER orientation fix has been applied
  const modelBounds = useMemo(() => {
    // Force world matrix update so the rotation is baked into the box
    scene.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(scene);
    return box;
  }, [scene]);

  // Compute scale to match mannequin height
  const modelScale = useMemo(() => {
    const modelHeight = modelBounds.max.y - modelBounds.min.y;
    if (modelHeight <= 0) return 1;
    const targetHeight = 1.78 * mannequinSettings.figureHeight;
    return targetHeight / modelHeight;
  }, [modelBounds, mannequinSettings.figureHeight]);

  // Compute Y offset so feet sit at Y=0
  const yOffset = useMemo(() => {
    return -modelBounds.min.y * modelScale;
  }, [modelBounds, modelScale]);

  // ── Measure arm span from the rest pose and push into the store ──
  // The pattern-difficulty backend uses this to normalize "how far is the
  // next note for that hand" relative to the player's physical reach.
  // Re-runs whenever the bones are re-discovered or the height scale changes.
  useEffect(() => {
    const bones = bonesRef.current;
    const left = bones.leftHand;
    const right = bones.rightHand;
    if (!left || !right) return;

    // Force matrices up to date before sampling world positions.
    scene.updateMatrixWorld(true);
    const lp = new THREE.Vector3();
    const rp = new THREE.Vector3();
    left.getWorldPosition(lp);
    right.getWorldPosition(rp);

    // World positions are in the FBX's intrinsic scale; multiply by the
    // mannequin scale (which itself is calibrated so figureHeight = 1.78m
    // at scale 1.0) to recover real meters.
    const armSpan = lp.distanceTo(rp) * modelScale;
    if (Number.isFinite(armSpan) && armSpan > 0.3 && armSpan < 3.0) {
      setArmSpanMeters(armSpan);
    }
  }, [scene, modelScale, mannequinSettings.figureHeight, setArmSpanMeters]);

  // Temp vectors for IK (avoid per-frame allocation)
  const _worldPos = useMemo(() => new THREE.Vector3(), []);
  const _targetLocal = useMemo(() => new THREE.Vector3(), []);
  const _invMat = useMemo(() => new THREE.Matrix4(), []);

  // Helper: aim an upper arm bone so its chain reaches toward a world-space target
  const aimArmToward = useCallback((
    upperArm: THREE.Bone | null,
    targetWorld: THREE.Vector3,
    blendWeight: number,
  ) => {
    if (!upperArm) return;

    // Get current world position of the upper arm bone
    upperArm.getWorldPosition(_worldPos);

    // Convert target from scene world-space to the bone's parent local space
    if (upperArm.parent) {
      _invMat.copy(upperArm.parent.matrixWorld).invert();
      _targetLocal.copy(targetWorld).applyMatrix4(_invMat);
    } else {
      _targetLocal.copy(targetWorld);
    }

    // Get the bone's local position
    const boneLocal = upperArm.position.clone();

    // Direction from bone to target in parent-local space
    const dir = _targetLocal.clone().sub(boneLocal).normalize();

    // Build a quaternion that points the bone's "down" axis (negative Y)
    // toward the target. Arm bones typically extend along their local Y axis.
    const defaultDir = new THREE.Vector3(0, -1, 0);
    const targetQuat = new THREE.Quaternion().setFromUnitVectors(defaultDir, dir);

    // Blend between the current (dance) pose and the note-tracking pose
    upperArm.quaternion.slerp(targetQuat, blendWeight);
  }, [_worldPos, _targetLocal, _invMat]);

  useFrame((_state, delta) => {
    const store = useEditorStore.getState();
    const currentBeat = store.playback.currentBeat;
    const diff = store.beatmap?.difficulties[store.activeDifficulty];
    const notes = diff?.notes || [];
    const bpm = store.beatmap?.bpm || 120;
    const playing = store.playback.playing;
    const { anticipationBeats, lerpSpeed } = store.mannequinSettings;
    const bones = bonesRef.current;

    // ── Procedural dance animation ─────────────────────────────────
    if (playing && bpm > 0) {
      const beatsPerSec = bpm / 60;
      dancePhase.current += delta * beatsPerSec * Math.PI * 2;
      // Keep phase in reasonable range
      if (dancePhase.current > 10000) dancePhase.current -= 10000;
    }

    const p = dancePhase.current;

    // ── Group-level dance animation ───────────────────────────────────
    // Animate the whole group (position + rotation) rather than individual
    // bones, since this FBX skeleton's pre-rotations make direct bone
    // manipulation unreliable. This gives a natural sway/bob effect.
    const group = groupRef.current;
    if (group) {
      const weightPhase = Math.sin(p * 0.5);
      const beatPulse = Math.sin(p);

      // Side-to-side sway
      group.position.x = weightPhase * 0.04;
      // Vertical bounce on the beat
      group.position.y = yOffset + Math.abs(beatPulse) * -0.02;
      // Z stays at FIGURE_Z
      group.position.z = FIGURE_Z;

      // Subtle rotations (Math.PI base = face forward toward camera)
      group.rotation.y = Math.PI + weightPhase * 0.06;    // twist
      group.rotation.z = weightPhase * 0.02;              // lean
      group.rotation.x = beatPulse * 0.015;               // nod
    }

    // ── Green note hand assignment ──────────────────────────────────
    const nextGreen = findNextGreenNote(notes, currentBeat, anticipationBeats);
    if (nextGreen) {
      const greenId = nextGreen.note_id || `${nextGreen.time_ms}`;
      if (greenId !== lastGreenNoteId.current) {
        greenHandAssignment.current = Math.random() < 0.5 ? 'left' : 'right';
        lastGreenNoteId.current = greenId;
      }
    } else if (greenHandAssignment.current !== 'none') {
      greenHandAssignment.current = 'none';
      lastGreenNoteId.current = '';
    }

    // ── Find next notes per hand ────────────────────────────────────
    const leftActiveRail = findActiveRailNote(notes, currentBeat, 'left', bpm, greenHandAssignment.current);
    const rightActiveRail = findActiveRailNote(notes, currentBeat, 'right', bpm, greenHandAssignment.current);
    const leftNote = leftActiveRail || findNextNoteForHand(notes, currentBeat, 'left', anticipationBeats, greenHandAssignment.current);
    const rightNote = rightActiveRail || findNextNoteForHand(notes, currentBeat, 'right', anticipationBeats, greenHandAssignment.current);

    leftNoteRef.current = leftNote;
    rightNoteRef.current = rightNote;

    // ── Compute hand targets (with rail following) ──────────────────
    if (leftNote) {
      const railPos = leftNote.has_rail ? getRailPositionAtBeat(leftNote, currentBeat, bpm) : null;
      leftHandTarget.current.copy(railPos || noteToWorld(leftNote));
    } else {
      leftHandTarget.current.copy(REST_LEFT);
    }

    if (rightNote) {
      const railPos = rightNote.has_rail ? getRailPositionAtBeat(rightNote, currentBeat, bpm) : null;
      rightHandTarget.current.copy(railPos || noteToWorld(rightNote));
    } else {
      rightHandTarget.current.copy(REST_RIGHT);
    }

    // Smooth interpolation
    const lerpFactor = 1 - Math.exp(-lerpSpeed * delta);
    leftHandSmoothed.current.lerp(leftHandTarget.current, lerpFactor);
    rightHandSmoothed.current.lerp(rightHandTarget.current, lerpFactor);

    // ── Position hand glow spheres at smoothed hand targets ─────────
    if (leftHandGlowRef.current) {
      leftHandGlowRef.current.position.copy(leftHandSmoothed.current);
      const nc = getNoteColorsFromSettings(useEditorStore.getState().gameplaySettings);
      const leftColor = leftNote ? (nc[leftNote.note_type] || nc.left_hand) : nc.left_hand;
      const lMat = leftHandGlowRef.current.material as THREE.MeshStandardMaterial;
      lMat.color.set(leftColor);
      lMat.emissive.set(leftColor);
    }
    if (rightHandGlowRef.current) {
      rightHandGlowRef.current.position.copy(rightHandSmoothed.current);
      const nc2 = getNoteColorsFromSettings(useEditorStore.getState().gameplaySettings);
      const rightColor = rightNote ? (nc2[rightNote.note_type] || nc2.right_hand) : nc2.right_hand;
      const rMat = rightHandGlowRef.current.material as THREE.MeshStandardMaterial;
      rMat.color.set(rightColor);
      rMat.emissive.set(rightColor);
    }

    // ── Move arm bones toward note targets (IK-like aiming) ──────────
    // After the procedural dance sets the base pose, we override the
    // upper arm bone rotation to aim the hand chain toward the target note.

    if (!groupRef.current) return;

    // Force world matrix update so aimArmToward gets correct transforms
    scene.updateMatrixWorld(true);

    const leftBlend = leftNote ? 0.7 : 0.0;
    const rightBlend = rightNote ? 0.7 : 0.0;

    if (leftBlend > 0) {
      aimArmToward(bones.leftUpperArm, leftHandSmoothed.current, leftBlend);
    }
    if (rightBlend > 0) {
      aimArmToward(bones.rightUpperArm, rightHandSmoothed.current, rightBlend);
    }
  });

  return (
    <>
      {/* The model group — scaled and positioned to match mannequin height */}
      <group ref={groupRef} position={[0, yOffset, FIGURE_Z]} rotation={[0, Math.PI, 0]} scale={[modelScale, modelScale, modelScale]}>
        <primitive object={scene} />
      </group>

      {/* Hand glow spheres — positioned in world space (outside the scaled group) */}
      <HandGlow colorRef={leftHandGlowRef} defaultColor={noteColorLeft} />
      <HandGlow colorRef={rightHandGlowRef} defaultColor={noteColorRight} />
    </>
  );
}
